import { useTranslation } from '@hbs-us/ui';

import { StatusBadge, type StatusTone } from '../../_money/StatusBadge.js';
import type { InvoiceListStatus } from '../contractsMirror.js';

/**
 * The invoice money-status pill (legacy `renderStatus()`). Maps the server
 * status union + the archived flag onto a locked status tone (UI-SPEC Color
 * "Status (invoice)" table) — Paid → green, Overdue → red, Partial/Archived →
 * amber, Draft/Unpaid → neutral. The status meaning is ALWAYS carried by the
 * badge TEXT, never color alone (A11Y-01).
 */

const STATUS_TONE: Record<InvoiceListStatus, StatusTone> = {
  Paid: 'success',
  Overdue: 'destructive',
  Partial: 'warning',
  Draft: 'neutral',
  Unpaid: 'neutral',
  // 'Sent' (D-02 ripple): the invoice went out for signature but carries no
  // payment yet — a distinct informational tone from the neutral Draft pill so
  // "sent, awaiting the customer" reads differently than "not sent yet".
  Sent: 'neutralBlue',
};

export function InvoiceStatusBadge({
  status,
  overdue,
  archived,
}: {
  status: InvoiceListStatus;
  /**
   * Server-derived overdue flag (separate from `status`, which the coarse API
   * never sets to `'Overdue'`). When set on an unpaid/partial invoice the badge
   * renders the red "Overdue" pill the legacy SSR shows (parity).
   */
  overdue?: boolean;
  archived?: boolean;
}) {
  const { t } = useTranslation();

  if (archived) {
    return <StatusBadge tone="warning">{t('invoices:status.archived')}</StatusBadge>;
  }
  // The server splits overdue into a flag + a Paid/Partial/Unpaid status; fold it
  // back into the displayed status so an overdue invoice shows the destructive
  // "Overdue" pill (legacy `invoiceStatus()` returns the literal 'Overdue').
  const displayStatus: InvoiceListStatus =
    overdue && (status === 'Unpaid' || status === 'Partial') ? 'Overdue' : status;
  return (
    <StatusBadge tone={STATUS_TONE[displayStatus]}>
      {t(`invoices:status.${displayStatus}`)}
    </StatusBadge>
  );
}
